<template>
  <div class="layout-default--wrap">
    <app-toolbar @open-notifications="showNotificationsDialog = true">
      <template v-slot:pageTitle>
        <slot name="pageTitle" />
      </template>
      <template v-slot:toolbar>
        <slot name="toolbar" />
      </template>
      <template v-slot:pageAction>
        <slot name="pageAction" />
      </template>
    </app-toolbar>
    <app-content>
      <slot></slot>
    </app-content>
    <v-btn
      v-if="floatingButton"
      color="primary"
      fixed
      bottom
      right
      fab
      large
      class="elevation-0 mb-8"
      @click="$emit('floating-button-action')"
    >
      <v-icon size="24">mdi-plus</v-icon>
    </v-btn>
    <v-snackbar :value="!!notificationSnackbarText" @input="onSnackbarInput">
      <div v-html="notificationSnackbarText"></div>
      <template v-slot:action="{ attrs }">
        <v-btn
          color="blue"
          text
          v-bind="attrs"
          @click="hideSnackbarNotification"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <notifications-dialog
      :show-dialog="showNotificationsDialog"
      @close-dialog="showNotificationsDialog = false"
      @open-settings="openSettings"
    />
    <v-dialog
      v-model="showNotificationsSettingsDialog"
      persistent
      max-width="390"
      @click:outside="onClickOutside"
      @keydown="onKeydown"
    >
      <v-card>
        <v-card-title class="headline"
          >Push notifications settings</v-card-title
        >
        <v-card-text>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Activities</h4>
              <p>Notify me when Participant completes activity</p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.routines"
            ></v-switch>
          </div>
          <div v-if="isAdvisor" class="settings-row">
            <div class="settings-desc">
              <h4>New login</h4>
              <p>Notify me when Participant logs in for the first time</p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.newLogin"
            ></v-switch>
          </div>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Smart Survey</h4>
              <p>
                Notify me when Participant completes or updates Smart Survey
              </p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.smartSurvey"
            ></v-switch>
          </div>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Journal</h4>
              <p>
                Notify me when Participant adds an entry or comments in Journal
              </p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.journal"
            ></v-switch>
          </div>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Swaps</h4>
              <p>Notify me when Participant adds an entry or notes in Swaps</p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.swap"
            ></v-switch>
          </div>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Browser notifications</h4>
              <p>
                Change browser push notification settings<br />
                <span v-if="browserNotificationsChanged" class="reload"
                  >Refresh the page to apply changes</span
                >
              </p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="browserPushNotifications"
              @click="browserNotificationsChanged = true"
            ></v-switch>
          </div>
          <h3 class="settings-headline">In App notifications</h3>
          <div class="settings-row">
            <div class="settings-desc">
              <h4>Autodelete read notifications</h4>
              <p>Delete my notifications that are 30 days old or older</p>
            </div>
            <v-switch
              :ripple="false"
              color="accent"
              v-model="settings.autoDelete"
            ></v-switch>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn class="rounded-pill" text @click="closeSettings">Cancel</v-btn>
          <v-btn
            class="rounded-pill elevation-0"
            color="accent"
            @click="saveSettings"
            >Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex'
import AppToolbar from '@/components/layout/AppToolbar.vue'
import AppContent from '@/components/layout/AppContent.vue'
import NotificationsDialog from '@/components/dialogs/NotificationsDialog.vue'

export default {
  name: 'LayoutDefault',
  props: {
    floatingButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showNotificationsDialog: false,
      showNotificationsSettingsDialog: false,
      settings: {},
      browserPushNotifications: null,
      browserNotificationsChanged: false,
    }
  },
  components: {
    AppToolbar,
    AppContent,
    NotificationsDialog,
  },
  watch: {
    notificationsSettings(newValue) {
      if (newValue) {
        this.settings = { ...this.notificationsSettings }
      }
    },
    browserPushNotifications(newValue, oldValue) {
      this.setBrowserNotifications(newValue)
    },
  },
  mounted() {
    this.settings = { ...this.notificationsSettings }
    this.browserPushNotifications = this.browserNotifications
  },
  computed: {
    ...mapState('appStates', ['notificationSnackbarText']),
    ...mapState('userStates', [
      'notificationsSettings',
      'browserNotifications',
    ]),
    ...mapState('dictionariesStates', ['dictionaryPracticeUserRole']),
    ...mapGetters('userStates', ['getRolename']),
    isAdvisor() {
      return (
        this.dictionaryPracticeUserRole[this.getRolename] ===
        this.dictionaryPracticeUserRole.Advisor
      )
    },
  },
  methods: {
    ...mapMutations('appStates', ['hideSnackbarNotification']),
    ...mapActions('userStates', [
      'getNotificationsSettings',
      'saveNotificationsSettings',
    ]),
    ...mapMutations('userStates', ['setBrowserNotifications']),
    onSnackbarInput(isVisible) {
      if (!isVisible && this.notificationSnackbarText) {
        this.hideSnackbarNotification()
      }
    },
    openSettings() {
      this.getNotificationsSettings().then(() => {
        this.showNotificationsSettingsDialog = true
      })
    },
    saveSettings() {
      this.saveNotificationsSettings(this.settings).then(() => {
        this.showNotificationsSettingsDialog = false
      })
    },
    closeSettings() {
      this.showNotificationsSettingsDialog = false
    },
    onClickOutside() {
      this.closeSettings()
    },
    onKeydown(key) {
      if (key.code === 'Escape' && key.isTrusted) this.closeSettings()
    },
  },
}
</script>

<style lang="scss">
@import '@/styles/variables.scss';

.layout-default {
  &--wrap {
    height: 100%;
    display: flex;
    flex: 1 0 auto;
    width: 100%;
  }
}
.settings {
  &-row {
    display: flex;
    align-items: center;
  }
  &-desc {
    flex-grow: 1;
    .reload {
      color: $bh-red--dark;
    }
  }
  &-headline {
    color: $bh-black;
    font-weight: 500;
    font-size: 1.5rem;
  }
}
</style>
